import React, { useEffect } from "react";
import { Outlet, Navigate, useLocation, useNavigate } from "react-router-dom";
import { setTenantNameAction } from "../store/actions/actionCreators/commonActionCreator";
import isTokenAvailable from "../utils/isTokenAvailable";
import { useDispatch } from "react-redux";
import getTenantName from "../utils/getTenantName";
import { preLoginActionHandler } from "../store/actions/actionHandlers/loginActionHandler";

// Reserved top-level URL segments that must never be treated as a tenant slug.
// Without this guard, ProtectedRoute writes whatever is in the URL's first
// segment into localStorage.tenantName, which corrupts the stored tenant when
// a SAML/AAD callback lands on a malformed URL like
// `/checkTenantpageaadlogin/<token>` (SAML server concatenates `aadlogin/<token>`
// onto a `web=` value that has no trailing slash).
const RESERVED_SLUGS = [
  "",
  "aadlogin",
  "checkTenantpage",
  "noPageFound",
  "nopageFound",
  "prelogin",
  "footer",
  "login",
];

const isReservedOrInvalidSlug = (slug) => {
  if (!slug) return true;
  if (RESERVED_SLUGS.includes(slug)) return true;
  // Catches the malformed SAML callback first segment, e.g. `checkTenantpageaadlogin`.
  if (slug.includes("aadlogin")) return true;
  return false;
};

const ProtectedRoute = () => {
  let token = isTokenAvailable();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tenantName = getTenantName(dispatch, location);

  useEffect(() => {
    const urlSlug = location.pathname.split("/")[1];

    // SAML/AAD callback delivered on a malformed URL such as
    // `/checkTenantpageaadlogin/<token>`. Extract the token portion and
    // redirect to the canonical `/aadlogin/<token>` route WITHOUT touching
    // localStorage.tenantName (otherwise the garbage slug would overwrite
    // the real tenant name and cause a bogus second preLogin call).
    if (urlSlug && urlSlug.includes("aadlogin") && urlSlug !== "aadlogin") {
      const splitURL = location.pathname.split("aadlogin");
      navigate(`/aadlogin${splitURL[1] || ""}`);
      return;
    }

    // Only persist the URL slug as tenantName when it actually looks like a
    // tenant slug (not a reserved app route).
    if (!isReservedOrInvalidSlug(urlSlug)) {
      if (!tenantName) {
        localStorage.setItem("tenantName", urlSlug);
        dispatch(setTenantNameAction(urlSlug));
      }
      if (location.pathname === `/${tenantName}`) {
        localStorage.setItem("tenantName", urlSlug);
        navigate("/checkTenantpage");
      }
      if (location.pathname !== `/${tenantName}`) {
        localStorage.setItem("tenantName", urlSlug);
      }
    }
  }, [location, navigate, dispatch, tenantName]);

  return token && localStorage?.getItem("tenantName") ? (
    <Outlet />
  ) : (
    <Navigate to="/checkTenantpage" />
  );
};

export default ProtectedRoute;
